import { useTranslation } from "react-i18next";

import Button from "@/components/forms/Button";

import { useGroupJoinPanel } from "../../hooks";
import type { Conversation } from "../../types";
import { GroupSettingsSkeleton } from "../group-settings/GroupUserListSkeleton";
import ConversationChat from "../messages/ConversationChat";
import GroupJoinFallbackView from "./GroupJoinFallbackView";

type GroupJoinPanelProps = {
  token: string;
  onOpenConversation: (conversation: Conversation<true, true, true>) => void;
  onClose?: () => void;
  showBackButton?: boolean;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  messageInputClassName?: string;
  messageInputVariant?: "fullscreen" | "compact";
};

export default function GroupJoinPanel({
  token,
  onOpenConversation,
  onClose,
  showBackButton = true,
  open = true,
  setOpen = () => {},
  messageInputClassName,
  messageInputVariant = "compact",
}: GroupJoinPanelProps) {
  const { t } = useTranslation("messenger.user");
  const {
    preview,
    isLoading,
    isError,
    joinGroup,
    syntheticConversation,
    handleJoin,
  } = useGroupJoinPanel(token, onOpenConversation);

  if (isLoading || (preview && !preview.can_join)) {
    return <GroupSettingsSkeleton />;
  }

  if (isError || !preview) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-sm text-gray-600">
          {t("Failed to load group invite")}
        </p>
        {showBackButton && onClose ? (
          <Button color="secondary-gray" onClick={onClose}>
            {t("Back", { ns: "core.shared" })}
          </Button>
        ) : null}
      </div>
    );
  }

  const joinButton = (
    <div className="border-t border-gray-200 p-4">
      <div className={messageInputClassName}>
        <Button
          color="secondary-color"
          className="w-full"
          isLoading={joinGroup.isPending}
          onClick={handleJoin}
        >
          {t("Join")}
        </Button>
      </div>
    </div>
  );

  if (syntheticConversation) {
    return (
      <ConversationChat
        open={open}
        setOpen={setOpen}
        conversation={syntheticConversation}
        onClose={onClose ?? (() => {})}
        showBackButton={showBackButton}
        showGroupMenu={false}
        messageInputClassName={messageInputClassName}
        messageInputVariant={messageInputVariant}
        bottomSlot={joinButton}
      />
    );
  }

  return (
    <GroupJoinFallbackView
      preview={preview}
      open={open}
      setOpen={setOpen}
      onClose={onClose}
      showBackButton={showBackButton}
      bottomSlot={joinButton}
    />
  );
}
